"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import {
  clearStoredToken,
  fetchWithAuth,
  getStoredToken,
} from "@/lib/api-client";

type SubscriptionStatus = {
  status: "trialing" | "active" | "canceled" | "expired" | string;
  plan: string;
  active: boolean;
  trial_start_date: string | null;
  trial_end: string | null;
  days_left_in_trial: number | null;
};

export function useSubscription() {
  const [data, setData] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const redirectRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);

    const token = getStoredToken();
    if (!token) {
      setData(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetchWithAuth("/subscription/status");

      if (response.status === 401) {
        clearStoredToken();
        setData(null);
        if (
          typeof window !== "undefined" &&
          !redirectRef.current &&
          window.location.pathname !== "/login"
        ) {
          redirectRef.current = true;
          window.location.href = "/login";
        }
        return;
      }

      if (!response.ok) {
        setError("Unable to load subscription status.");
        setData(null);
        return;
      }
      const payload = (await response.json()) as SubscriptionStatus;
      setData(payload);
    } catch (err) {
      console.error(err);
      setError("Unable to load subscription status.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}

